import React, { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import api, { fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChainSteps } from "@/pages/hris/ApprovalQueue";

const statusStyle = {
  "Approved": "bg-emerald-50 text-emerald-700",
  "Rejected": "bg-red-50 text-red-700",
  "Pending Approval": "bg-amber-50 text-amber-700",
  "Cancelled": "bg-slate-100 text-slate-600",
};
const THEMES = {
  VL: { icon: Icons.Palmtree, bar: "bg-blue-500", soft: "bg-blue-50 text-blue-700" },
  SL: { icon: Icons.HeartPulse, bar: "bg-rose-500", soft: "bg-rose-50 text-rose-700" },
};
const OTHER = [
  { icon: Icons.CalendarHeart, bar: "bg-violet-500", soft: "bg-violet-50 text-violet-700" },
  { icon: Icons.Sparkles, bar: "bg-amber-500", soft: "bg-amber-50 text-amber-700" },
  { icon: Icons.Leaf, bar: "bg-emerald-500", soft: "bg-emerald-50 text-emerald-700" },
  { icon: Icons.Baby, bar: "bg-sky-500", soft: "bg-sky-50 text-sky-700" },
];
const theme = (code, i) => THEMES[code] || OTHER[i % OTHER.length];
const n2 = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

const BalanceCard = ({ b, i }) => {
  const t = theme(b.leave_code, i);
  const I = t.icon;
  const pct = b.total > 0 ? Math.max(0, Math.min(100, (b.available / b.total) * 100)) : 0;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 transition-shadow hover:shadow-md" data-testid={`leave-balance-${b.leave_code}`}>
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${t.soft}`}><I className="h-5 w-5" /></div>
        <div className="min-w-0"><div className="text-sm font-semibold text-slate-900 truncate">{b.leave_type}</div><div className="text-[11px] uppercase tracking-wider text-slate-400">{b.leave_code}{b.year ? ` · ${b.year}` : ""}</div></div>
      </div>
      <div className="mt-4 flex items-end justify-between">
        <div><div className={`text-3xl font-semibold tabular-nums ${b.available < 0 ? "text-red-600" : "text-slate-900"}`} data-testid={`leave-available-${b.leave_code}`}>{n2(b.available)}</div><div className="text-[11px] text-slate-500">days available</div></div>
        <div className="text-right text-[11px] text-slate-500 leading-5"><div>Total <b className="text-slate-700 tabular-nums">{n2(b.total)}</b></div><div>Used <b className="text-slate-700 tabular-nums">{n2(b.used)}</b></div></div>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${t.bar} transition-all duration-700`} style={{ width: `${pct}%` }} /></div>
      <div className="mt-3 flex flex-wrap gap-1.5 min-h-[20px]">
        {b.pending > 0 && <span className="rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-[10px] font-medium">{n2(b.pending)} pending</span>}
        {b.carry_active > 0 && <span className="rounded-full bg-indigo-50 text-indigo-700 px-2 py-0.5 text-[10px] font-medium" data-testid={`leave-carry-${b.leave_code}`}>Carry over {n2(b.carry_active)} until {fmtDate(b.carry_over_expiry)}</span>}
        {b.validity_end && <span className="rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[10px] font-medium">Valid until {fmtDate(b.validity_end)}</span>}
      </div>
    </div>
  );
};

function LeaveTable({ rows, canApprove, onAction, tid, myEmpId, isSuper }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid={tid}>
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead>Reference</TableHead>
            <TableHead className="hidden md:table-cell">Employee</TableHead>
            <TableHead>Leave Type</TableHead>
            <TableHead className="hidden lg:table-cell">Dates</TableHead>
            <TableHead>Days</TableHead>
            <TableHead className="hidden md:table-cell">Pay</TableHead>
            <TableHead>Status</TableHead>
            {canApprove && <TableHead className="text-right">Action</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={canApprove ? 8 : 7} className="text-center text-slate-400 py-10"><Icons.CalendarX2 className="h-6 w-6 mx-auto mb-2 text-slate-300" />No leave requests yet.</TableCell></TableRow>}
          {rows.map((r) => (
            <TableRow key={r.id} className="hover:bg-slate-50/70" data-testid={`leave-row-${r.reference}`}>
              <TableCell className="font-mono text-xs text-slate-500">{r.reference}</TableCell>
              <TableCell className="hidden md:table-cell text-sm">{r.employee_name}</TableCell>
              <TableCell className="text-sm"><span className="font-medium text-slate-800">{r.leave_type || r.leave_code}</span><span className="ml-1.5 text-[10px] text-slate-400">{r.leave_code}</span></TableCell>
              <TableCell className="hidden lg:table-cell text-xs text-slate-500">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</TableCell>
              <TableCell className="text-sm tabular-nums">{r.days}{r.half_day ? <span className="ml-1 text-[10px] text-slate-400">half day</span> : null}</TableCell>
              <TableCell className="hidden md:table-cell text-xs">{r.with_pay === false ? <span className="text-slate-500">Without pay</span> : <span className="text-emerald-700">With pay</span>}</TableCell>
              <TableCell><Badge className={`${statusStyle[r.status] || "bg-slate-100"} hover:opacity-100 text-[10px]`}>{r.status}</Badge><ChainSteps chain={r.approval_chain} /></TableCell>
              {canApprove && (
                <TableCell className="text-right">
                  {r.status === "Pending Approval" && (!(r.approval_chain || []).some((s) => s.approver_user_id) || r.current_approver_id === myEmpId || isSuper) ? (
                    <div className="flex gap-1.5 justify-end">
                      <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => onAction(r, "approve")} data-testid={`approve-${r.reference}`}>Approve</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => onAction(r, "reject")} data-testid={`reject-${r.reference}`}>Reject</Button>
                    </div>
                  ) : <span className="text-xs text-slate-400">—</span>}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

const BLANK = { leave_type_id: "", start_date: "", end_date: "", reason: "", with_pay: true, half_day: false, attachment_url: "" };

const ApplyDialog = ({ open, setOpen, types, balances, approver, onSaved }) => {
  const [form, setForm] = useState(BLANK);
  useEffect(() => { if (open) setForm({ ...BLANK, leave_type_id: types[0]?.id || "" }); }, [open, types]);
  const bal = balances.find((b) => b.leave_type_id === form.leave_type_id);
  const days = form.half_day ? 0.5 : (!form.start_date || !form.end_date) ? 1 : Math.max(1, Math.round((new Date(form.end_date) - new Date(form.start_date)) / 86400000) + 1);
  const left = bal ? bal.available - bal.pending - days : null;
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/hris/requests/upload", fd); setForm((f) => ({ ...f, attachment_url: data.url })); toast.success("Attached"); } catch { toast.error("Upload failed"); }
  };
  const submit = async () => {
    try { await api.post("/leave/requests", { ...form, end_date: form.half_day ? form.start_date : form.end_date, days }); toast.success("Leave request submitted"); setOpen(false); onSaved(); } catch (err) { toast.error(err.response?.data?.detail || "Failed to file leave"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-2xl" data-testid="leave-apply-dialog">
        <DialogHeader><DialogTitle>Apply Leave</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2" data-testid="leave-approver"><Icons.UserCheck className="h-4 w-4 text-slate-400" /><span className="text-xs text-slate-500">Approver</span><span className="text-sm font-medium text-slate-800">{approver?.name || "—"}</span></div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-4 items-end">
            <div className="space-y-2"><Label>Leave Type</Label>
              <Select value={form.leave_type_id} onValueChange={(v) => setForm({ ...form, leave_type_id: v })}>
                <SelectTrigger data-testid="leave-type-select"><SelectValue placeholder="--Select--" /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`leave-type-option-${t.code}`}>{t.name} ({t.code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 pb-2.5 cursor-pointer"><Checkbox checked={form.with_pay} onCheckedChange={(c) => setForm({ ...form, with_pay: !!c })} data-testid="leave-with-pay" /><span className="text-sm text-slate-700">With Pay</span></label>
            <label className="flex items-center gap-2 pb-2.5 cursor-pointer"><Checkbox checked={form.half_day} onCheckedChange={(c) => setForm({ ...form, half_day: !!c })} data-testid="leave-half-day" /><span className="text-sm text-slate-700">Halfday</span></label>
          </div>
          {bal && (
            <div className="grid grid-cols-4 gap-2 text-center" data-testid="leave-apply-summary">
              {[["Available", bal.available], ["Pending", bal.pending], ["This request", days], ["After", left]].map(([l, v]) => (
                <div key={l} className={`rounded-lg border px-2 py-2 ${l === "After" && v < 0 ? "border-red-200 bg-red-50" : "border-slate-200"}`}><div className="text-[10px] uppercase tracking-wide text-slate-500">{l}</div><div className={`text-base font-semibold tabular-nums ${l === "After" && v < 0 ? "text-red-600" : "text-slate-900"}`}>{n2(v)}</div></div>
              ))}
            </div>
          )}
          {bal?.carry_active > 0 && <p className="text-[11px] text-indigo-700">Your {n2(bal.carry_active)} carried-over day(s) will be used first (valid until {fmtDate(bal.carry_over_expiry)}).</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Date From</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="leave-start-date" /></div>
            <div className="space-y-2"><Label>Date To</Label><Input type="date" value={form.half_day ? form.start_date : form.end_date} min={form.start_date || undefined} onChange={(e) => setForm({ ...form, end_date: e.target.value })} disabled={form.half_day} data-testid="leave-end-date" /></div>
          </div>
          <div className="space-y-2"><Label>Reason</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason for leave…" data-testid="leave-reason" /></div>
          <div className="space-y-2"><Label>Attachment</Label><Input type="file" onChange={(e) => upload(e.target.files[0])} data-testid="leave-attachment" />{form.attachment_url && <span className="text-xs text-emerald-600">File attached ✓</span>}</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="leave-close">Close</Button>
          <Button onClick={submit} disabled={!form.leave_type_id || !form.start_date || (!form.half_day && !form.end_date)} className="bg-blue-600 hover:bg-blue-700" data-testid="leave-submit">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Leave() {
  const { hasPerm, user } = useAuth();
  const [balances, setBalances] = useState([]);
  const [types, setTypes] = useState([]);
  const [mine, setMine] = useState([]);
  const [stF, setStF] = useState("All");
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [approver, setApprover] = useState(null);
  const canApprove = hasPerm("leave.approve") || team.length > 0;
  const pendingTeam = team.filter((t) => t.status === "Pending Approval").length;

  const load = useCallback(() => {
    api.get("/leave/balances").then(({ data }) => setBalances(data));
    api.get("/leave/types").then(({ data }) => setTypes(data));
    api.get("/leave/requests?scope=me").then(({ data }) => setMine(data));
    api.get("/leave/requests?scope=team").then(({ data }) => setTeam(data)).catch(() => {});
  }, []);

  useEffect(() => { api.get("/hris/my-approver").then(({ data }) => setApprover(data)).catch(() => {}); load(); }, [load]);

  const onAction = async (r, action) => {
    try { const { data } = await api.post(`/leave/requests/${r.id}/${action}`, { reason: "" }); toast.success(data?.next_approver ? `Approved — forwarded to ${data.next_approver}` : `Leave ${action === "approve" ? "approved" : "rejected"}`); load(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="uemp-fade-in">
      <PageHeader title="Leave Management" subtitle="File leave, track balances and approvals"
        actions={<Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="leave-apply-button"><Icons.CalendarPlus className="h-4 w-4 mr-1.5" /> File Leave</Button>} />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6 stagger" data-testid="leave-balances">
        {balances.map((b, i) => <BalanceCard key={b.leave_type_id} b={b} i={i} />)}
        {!balances.length && <div className="col-span-full rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-500">No leave balances yet. Please contact HR.</div>}
      </div>
      <Tabs defaultValue="mine">
        <TabsList>
          <TabsTrigger value="mine" data-testid="leave-tab-mine">My Requests</TabsTrigger>
          {canApprove && <TabsTrigger value="team" data-testid="leave-tab-team">Team Approvals {pendingTeam > 0 && <Badge className="ml-1.5 bg-amber-500 text-white text-[10px] h-4">{pendingTeam}</Badge>}</TabsTrigger>}
        </TabsList>
        <TabsContent value="mine">
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="leave-mine-filters">{["All", "Pending Approval", "Approved", "Rejected", "Cancelled"].map((s) => (
            <button key={s} onClick={() => setStF(s)} className={`text-[11px] rounded-full px-2.5 py-1 border ${stF === s ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`} data-testid={`leave-mine-filter-${s.split(" ")[0]}`}>
              {s === "Pending Approval" ? "Pending" : s} {s === "All" ? mine.length : mine.filter((r) => r.status === s).length}</button>))}</div>
          <LeaveTable rows={stF === "All" ? mine : mine.filter((r) => r.status === stF)} tid="leave-mine-table" />
        </TabsContent>
        {canApprove && <TabsContent value="team"><LeaveTable rows={team} canApprove onAction={onAction} tid="leave-team-table" myEmpId={user?.employee_id} isSuper={(user?.role || "").toUpperCase() === "SUPER ADMIN"} /></TabsContent>}
      </Tabs>
      <ApplyDialog open={open} setOpen={setOpen} types={types} balances={balances} approver={approver} onSaved={load} />
    </div>
  );
}
